import type { MobileForensicFamily, MobileForensicFamilyEntry, MobileForensicViewKind } from '@tranquility-ai/theme-components';
import type { MobileArtifactFamily, MobileUiConfigArtifact, MobileUiConfigResponse } from '@/shared/mobile-service/api/cors-mobile-api';

const VIEW_KINDS: MobileForensicViewKind[] = ['table', 'messages', 'map', 'gallery', 'timeline', 'cards', 'record'];

const FAMILY_ORDER: MobileForensicFamily[] = [
  'message',
  'call',
  'contact',
  'location',
  'media',
  'web',
  'app',
  'account',
  'financial',
  'system',
  'other',
];

export function artifactFamily(artifact: MobileUiConfigArtifact): MobileForensicFamily {
  const family = artifact.front_end?.meta?.family;
  if (family && FAMILY_ORDER.includes(family)) return family;
  return 'other';
}

export function artifactViews(artifact: MobileUiConfigArtifact): MobileForensicViewKind[] {
  const named = (artifact.front_end?.views ?? [])
    .map((view) => view.name)
    .filter((name): name is MobileForensicViewKind => VIEW_KINDS.includes(name as MobileForensicViewKind));
  return named.length > 0 ? named : ['table'];
}

/** One nav entry per family. Types with no family land in `other`. */
export function familyRegistry(
  uiConfig: MobileUiConfigResponse | undefined,
  counts?: Map<string, number>,
): MobileForensicFamilyEntry[] {
  const grouped = new Map<MobileForensicFamily, MobileForensicFamilyEntry>();
  for (const artifact of Object.values(uiConfig?.artifacts ?? {})) {
    const family = artifactFamily(artifact);
    const entry = grouped.get(family) ?? { id: family, count: 0, types: [] };
    const count = counts?.get(artifact.graphql_type);
    entry.types.push({
      id: artifact.graphql_type,
      label: artifact.title || artifact.graphql_type,
      count,
      views: artifactViews(artifact),
      icon: artifact.front_end?.meta?.icon,
    });
    entry.count = (entry.count ?? 0) + (count ?? 0);
    grouped.set(family, entry);
  }
  return FAMILY_ORDER.filter((family) => grouped.has(family)).map((family) => {
    const entry = grouped.get(family)!;
    entry.types.sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.label.localeCompare(b.label));
    return entry;
  });
}

export function isMessageArtifact(artifact: MobileUiConfigArtifact): boolean {
  return artifactFamily(artifact) === 'message' || artifactViews(artifact).includes('messages');
}

export type { MobileArtifactFamily };
